/**
 * Component for populating the data in public `Notification details`/`Request payment plan auto` panel
 */

import { Button, Col, Drawer, Form, Modal, Row, Table, Skeleton } from 'antd';
import {
    clone,
    forEach,
    get,
    includes,
    isEmpty,
    isUndefined,
    keys,
    last,
    map,
    sumBy,
    toNumber,
} from 'lodash';
import moment from 'moment-timezone';
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withDateFormatHandler } from '../../components/common/DateFormatHandler';
import { withNumberFormatHandler } from '../../components/common/NumberFormatHandler';
import {
    createPaymentPlanAction
} from '../../store/paymentPlans/actions';
import { PaymentPlan } from '../../store/paymentPlans/types';
import { getIsMobile } from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';

const ModalWithSpinner = lazy(
    () => import('../../components/common/ModalWithSpinner')
);

interface IProps {
    readonly visible: boolean;
    readonly closePanel?: (refetchList?: boolean) => void;
    readonly form?: any;
    readonly invoices: any[];
    readonly isNewInvoice: boolean;
    readonly paymentPlansData: { errorMessage?: any; paymentPlans?: PaymentPlan[] };
    readonly customer: {};
    readonly company: {};
    readonly formatCurrency?: (amount: number) => JSX.Element;
    readonly formatDateLocal: (
        date: any,
        fromFormat?: string | null,
        toFormat?: string | null
    ) => string;
    readonly totalPayment: number;
    readonly availableCredits: any[];
    readonly selectedRowKeys: string[];
    readonly selectedRowKeysCredits: string[];
    readonly payUsingCreditsHandler: (
        invoiceIds: string[],
        creditIds: string[],
        cardPaymentSelected?: string,
        isOverseas?: boolean,
        totalPaymentRemaining?: number,
        payByCreditCard?: boolean,
        customCallback?: () => void,
        shouldFetchUpdatedInvoices?: boolean
    ) => void;
    readonly processActionBasedOnPaymentGateway: (totalAmount: number, isDeposit?: boolean) => void;
}

const EXCLUDED_KEY = '0';

const getPaymentSchedules = (
    paymentPlans: PaymentPlan[] | undefined
) => {
    const paymentPlan = paymentPlans ? paymentPlans[0] : []
    return get(paymentPlan, "DepositAmount") > 0 ?
        get(paymentPlan, "PaymentPlanSchedules").slice(1) :
        get(paymentPlan, "PaymentPlanSchedules")
}

const NotificationPaymentPlanSelectAutoPanel: React.FC<IProps> = ({
    visible,
    closePanel,
    form,
    invoices,
    isNewInvoice,
    paymentPlansData,
    customer,
    company,
    formatCurrency,
    formatDateLocal,
    availableCredits,
    selectedRowKeys,
    selectedRowKeysCredits,
    payUsingCreditsHandler,
    processActionBasedOnPaymentGateway
}: IProps) => {
    const isMobile = getIsMobile();
    const panelTitle = 'Request a payment plan';
    const dispatch = useDispatch();
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [invoicesState, setInvoicesState] = useState<DynamicObject>({});
    const { validateFields, resetFields } = form;
    const paymentPlans = get(paymentPlansData, 'paymentPlans') || [];
    const paymentPlanSchedules = getPaymentSchedules(get(paymentPlansData, 'paymentPlans')) || [];
    const errorMessage = get(paymentPlansData, 'errorMessage');

    /**
     * Function called when drawer or modal is closed.
     */
    const handleClosePanel = () => {
        if (closePanel) closePanel();
    };

    /**
     * Listener function for when the panel's visibility changes.
     * Resets the form and selected keys if panel is closed.
     */
    const listenForClosingPanel = () => {
        if (!visible) {
            resetFields();
        }
    };

    useEffect(listenForClosingPanel, [visible]);

    /**
     * Function for setting the invoices used based on PaymentInstalmentFrequency property.
     */
    const checkInvoicesUsedBasedOnInstalmentNumber = () => {
        if (isEmpty(invoices) || isEmpty(selectedRowKeys) || isEmpty(paymentPlans)) return;

        const newInvState: DynamicObject =
            returnInvoiceStateGroupBasedOnList(invoices, paymentPlans);

        setInvoicesState(newInvState);
    };

    /**
     * Function that returns an object with InstalmentNumber as keys based on invoice list given
     */
    const returnInvoiceStateGroupBasedOnList = (invList: DynamicObject[], paymentPlans: DynamicObject[]) => {
        let newInvState: DynamicObject = {};

        forEach(invList, (inv: DynamicObject) => {
            forEach(paymentPlans, (paymentPlan: DynamicObject) => {
                if (includes(paymentPlan.InvoiceIds, inv.Id)) {
                    const insNumbUsed = paymentPlan.TotalSchedules
                    if (!newInvState[insNumbUsed]) {
                        newInvState[insNumbUsed] = [inv];
                    } else {
                        newInvState[insNumbUsed] = [
                            ...newInvState[insNumbUsed],
                            inv,
                        ];
                    }
                }
            });
        });
        return newInvState;
    };

    useEffect(checkInvoicesUsedBasedOnInstalmentNumber, [
        invoices,
        selectedRowKeys,
        paymentPlans
    ]);

    /**
     * Function for getting the panel data based on state.
     */
    const panelData = useMemo(() => {
        const invoicesStateClone = clone(invoicesState);

        let hasMultipleKeys
        if (paymentPlans != undefined) {
            hasMultipleKeys = paymentPlans.length > 1;
        }

        const invoiceIds: string[] = [];

        forEach(invoicesStateClone, (invList) => {
            forEach(invList, (inv) => {
                invoiceIds.push(inv.Id);
            });
        });

        const creditIds: string[] = [];
        forEach(availableCredits, (cred) => {
            if (includes(selectedRowKeysCredits, cred.Id)) {
                creditIds.push(cred.Id);
            }
        });

        const submitBtnDisabled = isEmpty(paymentPlans);

        return {
            invoicesStateClone,
            submitBtnDisabled,
            hasMultipleKeys,
            invoiceIds,
            paymentPlans,
            creditIds,
        };
    }, [invoicesState, availableCredits, selectedRowKeysCredits, paymentPlans]);


    /**
     * Function called when submitting the form.
     */
    const handleSubmitForm = () => {
        validateFields((err: any, _values: any) => {
            if (!err) {
                const {
                    invoicesStateClone,
                    creditIds,
                    invoiceIds,
                    hasMultipleKeys,
                    paymentPlans
                } = panelData;
                if (!isEmpty(creditIds)) {

                    payUsingCreditsHandler(
                        invoiceIds,
                        creditIds,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        (newInvoiceList?: DynamicObject[]) => {
                            const invListReturned: DynamicObject = {};

                            forEach(
                                invoicesStateClone,
                                (invList: DynamicObject[], usedSingleKey: string) => {
                                    const invoiceIds = invList.map(n => n.Id);
                                    const openInvoices = newInvoiceList ? newInvoiceList.filter(i => includes(invoiceIds, i.Id)) : [];

                                    if (openInvoices.length > 0) {
                                        invListReturned[usedSingleKey] = openInvoices;
                                    }
                                }
                            );

                            createPaymentPlan(paymentPlans, invListReturned);
                        },
                        hasMultipleKeys
                    );
                } else {
                    createPaymentPlan(paymentPlans, invoicesStateClone);
                }
            }
        });
    };

    const columns = [
        {
            title: '',
            dataIndex: 'LocalDueDateTime',
        },
        {
            title: '',
            dataIndex: 'TotalAmount',
        },
    ];

    const dataSource = map(paymentPlanSchedules, ({ ...data }: any) => {
        return {
            ...data,
            LocalDueDateTime: formatDateLocal(data.LocalDueDateTime),
            TotalAmount: formatCurrency
                ? formatCurrency(data.TotalAmount)
                : data.TotalAmount
        }
    });

    /**
     * Function for calling the action to create payment plan 
     */
    const createPaymentPlan = (
        paymentPlans: PaymentPlan[],
        invoiceListUsed?: DynamicObject
    ) => {
        // Credits has been applied and new invoice list will be given (if available)
        setSubmitLoading(true);
        const lastKeyUsed = last(keys(invoiceListUsed));
        let depositAmountToPay: number | undefined = undefined;

        forEach(
            invoiceListUsed,
            (invList: DynamicObject[], usedSingleKey: string) => {
                forEach(paymentPlans, paymentPlan => {
                    if (includes(paymentPlan.InvoiceIds, invList[0].Id)) {
                        const shouldClosePanel = usedSingleKey === lastKeyUsed;
                        if (isNewInvoice && !depositAmountToPay && paymentPlan.DepositAmount) {
                            const paidAmount = paymentPlan.TotalAmount - sumBy(invList, 'AmountOwing');
                            const depositRemaining = paymentPlan.DepositAmount && (paymentPlan.DepositAmount - paidAmount);
                            if (depositRemaining > 0) {
                                depositAmountToPay = depositRemaining;
                            }
                        }
                        (paymentPlan as any).InvoiceIds = map(invList, 'Id');
                        createPaymentPlanAPICall(
                            paymentPlan,
                            shouldClosePanel,
                            depositAmountToPay
                        );
                    }
                });
            }
        );
    };

    /**
     * Function for calling the action for creating the payment plan
     * @param invoiceIds
     * @param totalAmount
     * @param totalSchedules
     * @param shouldClosePanel
     */
    const createPaymentPlanAPICall = (
        paymentPlan: PaymentPlan,
        shouldClosePanel: boolean = true,
        depositAmountToPay: number | undefined
    ) => {
        if (paymentPlan != null) {
            dispatch(
                createPaymentPlanAction(paymentPlan, ({ IsSuccess, Messages }) => {
                    setSubmitLoading(false);
                    if (IsSuccess) {
                        if (shouldClosePanel) {
                            if (closePanel) closePanel(true);
                            if (depositAmountToPay) processActionBasedOnPaymentGateway(depositAmountToPay, true);
                        }
                    } else {
                        let errorMessageContent: any = `Failed to request payment plan!`;

                        if (!isEmpty(Messages)) {
                            errorMessageContent = map(
                                Messages,
                                (error: string, index: number) => (
                                    <div key={index}>{error}</div>
                                )
                            );
                        }

                        Modal.error({
                            title: 'Error',
                            content: errorMessageContent
                        });
                    }
                })
            );
        }
    };

    /**
     * Common function for formatting currency
     */
    const handleFormatCurrency = (value: number) => {
        if (formatCurrency) {
            return formatCurrency(value);
        }
    };

    const generateSectionWithLoading = (
        sectionComponent: any,
        loadingWidth: number,
        indicatorValue?: any
    ) => {
        if ((!indicatorValue)) {
            return (
                <Skeleton
                    className="notification-skeleton"
                    active={true}
                    loading
                    paragraph={true}
                    title={{ width: loadingWidth }}
                />
            );
        } else {
            return sectionComponent;
        }
    };

    /**
     * Function for populating the panel content (with loading skeletons/data).
     */
    const populatePanelContent = () => {
        const {
            hasMultipleKeys,
            submitBtnDisabled,
            paymentPlans
        } = panelData;
        if (errorMessage) {
            return (
                <Form className="form-inline-mb-0">
                    <Row>
                        <Col span={24}>
                            {errorMessage}
                        </Col>
                    </Row>
                </Form>
            );
        } else if (submitBtnDisabled) {
            return (
                <Form className="form-inline-mb-0">
                    <Row>
                        <Col span={24}>
                            These invoices are not eligible for a payment plan.
                        </Col>
                    </Row>
                </Form>
            );
        } else if (hasMultipleKeys) {
            return (
                <Form className="form-inline-mb-0">
                    <Row>
                        <Col span={24}>
                            The payment instalment timing varies for these
                            invoices. Once the payment plan is approved you
                            shall receive one or more emails outlining the
                            specific payment schedule.
                        </Col>
                    </Row>
                </Form>
            );
        } else {
            const paymentPlankeyUsed = get(paymentPlans, 0);
            const totalSchedule = paymentPlankeyUsed.TotalSchedules;
            let deposit: number | undefined = undefined;
            if (isNewInvoice) {
                deposit = paymentPlankeyUsed.DepositAmount;
            }
            const firstSchedule = deposit ? paymentPlankeyUsed.PaymentPlanSchedules[1] : paymentPlankeyUsed.PaymentPlanSchedules[0];
            const schedulePayment = firstSchedule && firstSchedule.TotalAmount;

            return (
                <Form className="form-inline-mb-0">
                    <Row>
                        <Col span={24}>
                            <Row>
                                <Col span={24} className="mb-10">
                                    <div>
                                        <b>
                                            This payment plan covers the
                                            selected invoices and credits.
                                        </b>
                                    </div>
                                    <div>
                                        <b>
                                            The total amount to pay is{' '}
                                            {handleFormatCurrency(
                                                paymentPlankeyUsed.TotalAmount
                                            )}.
                                        </b>
                                    </div>
                                    {
                                        !!deposit &&
                                        <div>
                                            <b>
                                                Firstly, you are required to pay a deposit amount of{' '}
                                                {handleFormatCurrency(
                                                    deposit
                                                )}{' '}.
                                            </b>
                                        </div>
                                    }
                                    <div>
                                        <b>
                                            {deposit ? 'Then you' : 'You'} are required to pay on the following schedule:
                                        </b>
                                    </div>
                                </Col>
                                <Col>
                                    <Table
                                        className="themed-table"
                                        showHeader={false}
                                        columns={columns}
                                        dataSource={dataSource}
                                        pagination={false}
                                    />
                                </Col>
                                <Col span={24} className="mt-20">
                                    <div>
                                        You shall receive an email with the
                                        payment schedule.
                                    </div>
                                    <div>
                                        Each time your instalment is due, you shall receive a reminder providing you with payment options.
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Form>
            );
        }
    };

    const { submitBtnDisabled } = panelData;

    return (
        <Row>
            <Col>
                {isMobile ? (
                    <div>
                        {visible && (
                            <Modal
                                className="modal-swapped-buttons modal-no-padding"
                                title={panelTitle}
                                visible={visible}
                                onOk={handleSubmitForm}
                                onCancel={handleClosePanel}
                                okButtonProps={{ disabled: submitBtnDisabled }}
                                okText="Request"
                                style={{ top: 12 }}
                            >
                                <div
                                    style={{
                                        maxHeight: window.innerHeight - 160,
                                        overflowY: 'auto',
                                        padding: 24,
                                    }}
                                >
                                    {
                                        generateSectionWithLoading(
                                            populatePanelContent(),
                                            250,
                                            paymentPlansData,
                                        )
                                    }
                                </div>
                            </Modal>
                        )}
                    </div>
                ) : (
                    <Drawer
                        title={panelTitle}
                        width={500}
                        onClose={handleClosePanel}
                        visible={visible}
                        className="notification-details-panel"
                        closable={false}
                        maskClosable={false}
                    >
                        {visible && (
                            <>
                                {generateSectionWithLoading(
                                    <><div>{populatePanelContent()}</div>
                                        <br />
                                        <Row>
                                            <Col className="ta-right" span={24}>
                                                <Button
                                                    className="mr-8"
                                                    type="primary"
                                                    onClick={handleSubmitForm}
                                                    disabled={submitBtnDisabled}
                                                >
                                                    Request
                                                </Button>
                                                <Button onClick={handleClosePanel}>
                                                    Cancel
                                                </Button>
                                            </Col>
                                        </Row></>,
                                    250,
                                    paymentPlansData,
                                )}

                            </>
                        )}
                    </Drawer>
                )}
            </Col>

            {submitLoading && (
                <Suspense fallback={null}>
                    <ModalWithSpinner
                        modalTitle="Requesting payment plan"
                        modalVisible={submitLoading}
                        displayMessage="Please wait while requesting the payment plan . . ."
                    />
                </Suspense>
            )}
        </Row>
    );
};

const NotificationPaymentPlanSelectAutoPanelForm = Form.create({
    name: 'notification-payment-plan-select-auto-panel-form',
})(NotificationPaymentPlanSelectAutoPanel);

export default withRouter(
    withNumberFormatHandler(
        withDateFormatHandler(NotificationPaymentPlanSelectAutoPanelForm)
    )
);
